<script lang="ts">
  import { Router, Route, navigate }  from 'svelte-routing';
  import { onMount }                  from 'svelte';
  import { addFormStyles }    from './components/ui/form-utils';

  /* stores ----------------------------------------------------------- */
  import { auth }            from './stores/auth';
  import { sessionManager }  from './stores/sessionManager';
  import { 
    screenSize, 
    isMobile, 
    isTablet,
    isDesktop,
    currentBreakpoint
  } from './stores/screenSize';

  /* pages ------------------------------------------------------------ */
  import Portfolio   from './components/portfolio/Portfolio.svelte';
  import CreateWork  from './components/create/CreateWork.svelte';

  /* modals ----------------------------------------------------------- */
  import AuthModal    from './components/auth/AuthModal.svelte';
  import SessionModal from './components/common/SessionModal.svelte';

  /* ------------------------------------------------------------------ */
  export let url = '';

  let mounted        = false;
  let showAuthModal  = false;
  let currentPath    = '/';           // updated on popstate

  /* ----------------------------- life-cycle ------------------------- */
  onMount(() => {
    // Add form styles for all forms in the application
    addFormStyles();
    
    auth.checkAuth();                 // populates $auth.isAuthenticated
    mounted = true;

    const pop = () => currentPath = window.location.pathname;
    window.addEventListener('popstate', pop);

    return () => {
      window.removeEventListener('popstate', pop);
    };
  });

  /* -------------------- reactive: open AuthModal -------------------- */
  $: if (
        mounted &&
        !$auth.isAuthenticated &&
        !showAuthModal &&
        !$sessionManager.showSessionModal
     ) {
       showAuthModal = true;          // force sign-in when necessary
     }

  /* -------------------------- helpers ------------------------------- */
  function handleNavigationClick (e: MouseEvent, path: string) {
    e.preventDefault();
    if (!$auth.isAuthenticated) {
      showAuthModal = true;
      return;
    }
    navigate(path);
  }

  function handleAuthSuccess () {
    showAuthModal = false;
    navigate(currentPath);            // return to the page the user wanted
  }
</script>

{#if mounted}
  <Router {url}>
    <main class="app-container" class:mobile={$isMobile} class:tablet={$isTablet} class:desktop={$isDesktop} data-breakpoint={$currentBreakpoint}>
      <Route path="/"       component={Portfolio} />
      <Route path="create"  component={CreateWork} />
    </main>
  </Router>
{/if}

<!-- --------------- Auth & Session Modals --------------------------- -->
{#if showAuthModal}
  <AuthModal
    on:close  ={() => showAuthModal = false}
    on:success={handleAuthSuccess}
  />
{/if}

{#if $sessionManager.showSessionModal}
  <SessionModal
    message={$sessionManager.lastError?.message
             ?? 'Your session has expired. Please log in again.'}
    on:close={() => {
      sessionManager.closeSessionModal();
      auth.logout();                  // <-- clears tokens & triggers watcher
    }}
  />
{/if}

<style>
  .app-container {
    position: relative;
    width: 100%;
    min-height: 100vh;
    --nav-width-desktop: 280px;
    --nav-width-tablet: 72px;
    --nav-width-large-desktop: 320px;
    --content-max-width: 1600px;
  }
  
  /* Apply responsive CSS variables for use throughout the app */
  .app-container.mobile {
    --nav-width: 0;
    --content-padding: 16px;
    --header-height: 64px;
    --footer-height: calc(68px + env(safe-area-inset-bottom, 0px)); /* For bottom navigation + iOS safe area */
  }
  
  .app-container.tablet {
    --nav-width: var(--nav-width-tablet);
    --content-padding: 24px;
    --header-height: 72px;
    --footer-height: 0;
  }
  
  .app-container.desktop {
    --nav-width: var(--nav-width-desktop);
    --content-padding: 32px;
    --header-height: 80px;
    --footer-height: 0;
  }
  
  /* Export breakpoint info to CSS custom properties for use in other components */
  .app-container[data-breakpoint="xxl"] {
    --nav-width: var(--nav-width-large-desktop);
    --content-padding: 40px;
  }
  
  /* These can be used in any component with:
     var(--nav-width), var(--content-padding), etc. */
</style>
